import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import store from './store/store';
import { initializeApp } from './store/slices/appSlice';
import { LanguageProvider } from './i18n';

// Components
import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import LoadingSpinner from './components/UI/LoadingSpinner';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/Auth/LoginPage';
import RegisterPage from './pages/Auth/RegisterPage';
import DashboardPageGlass from './pages/Dashboard/DashboardPageGlass';
import LawyerDashboard from './pages/Lawyer/LawyerDashboardGlass';
import LawyerSchedulePage from './pages/Lawyer/LawyerSchedulePage';
import LawyerReviewsPage from './pages/Lawyer/LawyerReviewsPage';
import LawyerProfileEditPage from './pages/Lawyer/LawyerProfileEditPage';
import AdminDashboard from './pages/Admin/AdminDashboardGlass';
import AIChatPage from './pages/AI/AIChatPage';
import AIChatPageGlass from './pages/AI/AIChatPageGlass';
import ConsultationsPage from './pages/Consultations/ConsultationsPage';
import ConsultationsPageGlass from './pages/Consultations/ConsultationsPageGlass';
import VideoCallPage from './pages/Consultations/VideoCallPage';
import ChatPage from './pages/Consultations/ChatPage';
import LawyersPage from './pages/Lawyers/LawyersPage';
import LawyersPageGlass from './pages/Lawyers/LawyersPageGlass';
import LawyerProfilePage from './pages/Lawyers/LawyerProfilePage';
import LawyerProfilePageGlass from './pages/Lawyers/LawyerProfilePageGlass';
import DocumentsPage from './pages/Documents/DocumentsPage';
import DocumentsPageGlass from './pages/Documents/DocumentsPageGlass';
import ProfilePageGlass from './pages/Profile/ProfilePageGlass';
import SettingsPageGlass from './pages/Settings/SettingsPageGlass';
import HelpPage from './pages/Help/HelpPage';
import ForgotPasswordPage from './pages/Auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/Auth/ResetPasswordPage';
import SpecializationsPageGlass from './pages/Admin/SpecializationsPageGlass';
import FavoritesPage from './pages/Client/FavoritesPage';
import PortfolioPage from './pages/Client/PortfolioPage';
import VerifyEmailPage from './pages/Auth/VerifyEmailPage';

// MaslaXat Premium Theme
import { axelionTheme } from './theme/axelionTheme';

const theme = axelionTheme;

// Query client configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    },
  },
});

// App content component
const AppContent = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, loading } = useSelector((state) => state.auth);

  useEffect(() => {
    dispatch(initializeApp());
  }, [dispatch]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ backgroundColor: '#F5F1EB' }}
      >
        <LoadingSpinner />
      </Box>
    );
  }

  return (
    <Router>
      <Routes>
        <Route path="/" element={
          isAuthenticated ? <Navigate to="/dashboard" /> : <Navigate to="/login" />
        } />

        <Route path="/login" element={!isAuthenticated ? <LoginPage /> : <Navigate to="/dashboard" />} />
        <Route path="/register" element={!isAuthenticated ? <RegisterPage /> : <Navigate to="/dashboard" />} />
        <Route path="/forgot-password" element={!isAuthenticated ? <ForgotPasswordPage /> : <Navigate to="/dashboard" />} />
        <Route path="/reset-password" element={!isAuthenticated ? <ResetPasswordPage /> : <Navigate to="/dashboard" />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />

        <Route element={
          <ProtectedRoute allowedRoles={['client']}>
            <Layout />
          </ProtectedRoute>
        }>
          <Route path="dashboard" element={<DashboardPageGlass />} />
          <Route path="ai-chat" element={<AIChatPageGlass />} />
          <Route path="consultations" element={<ConsultationsPageGlass />} />
          <Route path="lawyers" element={<LawyersPageGlass />} />
          <Route path="lawyers/:lawyerId" element={<LawyerProfilePage />} />
          <Route path="documents" element={<DocumentsPageGlass />} />
          <Route path="favorites" element={<FavoritesPage />} />
          <Route path="portfolio" element={<PortfolioPage />} />
          <Route path="profile" element={<ProfilePageGlass />} />
          <Route path="settings" element={<SettingsPageGlass />} />
          <Route path="help" element={<HelpPage />} />
        </Route>

        {/* Video call — accessible to both client and lawyer, fullscreen (no Layout) */}
        <Route path="/consultations/video/:consultationId" element={
          <ProtectedRoute allowedRoles={['client', 'lawyer']}>
            <VideoCallPage />
          </ProtectedRoute>
        } />

        {/* Chat — accessible to both client and lawyer */}
        <Route path="/consultations/chat/:consultationId" element={
          <ProtectedRoute allowedRoles={['client', 'lawyer']}>
            <ChatPage />
          </ProtectedRoute>
        } />

        <Route path="/lawyer/dashboard" element={
          <ProtectedRoute allowedRoles={['lawyer']}>
            <LawyerDashboard />
          </ProtectedRoute>
        } />
        <Route path="/lawyer/schedule" element={
          <ProtectedRoute allowedRoles={['lawyer']}>
            <LawyerSchedulePage />
          </ProtectedRoute>
        } />
        <Route path="/lawyer/profile/edit" element={
          <ProtectedRoute allowedRoles={['lawyer']}>
            <LawyerProfileEditPage />
          </ProtectedRoute>
        } />
        <Route path="/lawyer/reviews" element={
          <ProtectedRoute allowedRoles={['lawyer']}>
            <LawyerReviewsPage />
          </ProtectedRoute>
        } />

        <Route path="/admin/dashboard" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="/admin/specializations" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <SpecializationsPageGlass />
          </ProtectedRoute>
        } />

        <Route path="*" element={
          isAuthenticated ? <Navigate to="/dashboard" /> : <Navigate to="/login" />
        } />
      </Routes>
    </Router>
  );
};

// Main App component
function App() {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <LanguageProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <AppContent />
            <ToastContainer
              position="top-right"
              autoClose={4000}
              hideProgressBar={false}
              newestOnTop
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
              toastStyle={{
                backgroundColor: '#FFFFFF',
                color: '#2D2D2D',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(26, 26, 26, 0.08)',
                border: '1px solid #E8E4DE',
                fontFamily: '"Inter", sans-serif',
              }}
            />
          </ThemeProvider>
        </LanguageProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </Provider>
  );
}

export default App;
